import { AccountListingType, Box, Card, CardLogo, DetailRow, DetailRows, MaterialSymbol, withStyles } from 'vopay';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { NoFillButton, SideDrawer, Title, UtilButton } from '#shared/components/UI';
import { useAlertDialog } from '#shared/providers/AlertDialog';
import { useToast } from '#shared/providers/Toast';
import useDeleteBankAccount from '#shared/hooks/useApi/useDeleteBankAccount';
import useDeleteCreditCard from '#shared/hooks/useApi/useDeleteCreditCard';
import { getFormattedAccountNumber, getMaskedAccountLabel } from '../accountLabels';
import useAccountTypeLabels from '../useAccountTypeLabels';
import useCountryLabels from '../useCountryLabels';
import { AccountRow } from '../types';
import styles from './AccountDetailsDrawer.module.scss';

const cn = withStyles(styles);

export type AccountDetailsDrawerProps = {
  isOpen: boolean;
  account: AccountRow | null;
  onClose: () => void;
  onSetAsDefault: () => void;
  onDeleted: () => void;
  accountListingType: AccountListingType;
  /** Host permission for the Set as Default action (which permission applies is the host's call). */
  canSetDefault: boolean;
  /** Host permission for the Delete Account action (which permission applies is the host's call). */
  canDelete: boolean;
  /** Host controls rendered below the heading card (the host owns the account it opened the drawer for); disabled while the drawer's delete is in flight. */
  renderActions?: (opts: { disabled: boolean }) => ReactNode;
};

export default function AccountDetailsDrawer({
  isOpen,
  account,
  onClose,
  onSetAsDefault,
  onDeleted,
  accountListingType,
  canSetDefault,
  canDelete,
  renderActions,
}: AccountDetailsDrawerProps) {
  const alertDialog = useAlertDialog();
  const toast = useToast();
  const { deleting: isDeletingBankAccount, deleteBankAccount } = useDeleteBankAccount();
  const { deleting: isDeletingCreditCard, deleteCreditCard } = useDeleteCreditCard();
  const isDeleting = isDeletingBankAccount || isDeletingCreditCard;

  const { t } = useTranslation('translations', { keyPrefix: 'Shared.AccountDetailsDrawer' });
  const { t: tButtons } = useTranslation('translations', { keyPrefix: 'Shared.Buttons' });
  const { t: tErrors } = useTranslation('translations', { keyPrefix: 'Shared.Errors' });
  const { getAccountTypeLabel } = useAccountTypeLabels();
  const { getCountryLabel } = useCountryLabels();

  async function handleDeleteAccount() {
    if (!account) return;

    const result = await alertDialog.caution(
      t('DeleteConfirm.title', { defaultValue: 'Delete Account' }),
      t('DeleteConfirm.subTitle', {
        defaultValue: 'Are you sure you want to delete this account? This action cannot be undone.',
      }),
      {
        abortLabel: tButtons('cancel', { defaultValue: 'Cancel' }),
        confirmLabel: t('Button.delete', { defaultValue: 'Delete' }),
      },
    );

    if (result !== 'confirm') return;

    try {
      if (account.Type === 'Credit Card') {
        await deleteCreditCard({ CreditCardToken: account.CreditCardToken });
      } else {
        await deleteBankAccount({ Token: account.Token });
      }

      toast.success(t('DeleteConfirm.success', { defaultValue: 'Account deleted successfully.' }));
      onClose();
      onDeleted();
    } catch (error) {
      // The hook already logged. The backend's message is interpolated into localized framing rather
      // than shown bare — on its own it reads as an unattributed English string.
      await alertDialog.error(
        t('DeleteConfirm.errorTitle', { defaultValue: 'Unable to Delete Account' }),
        t('DeleteConfirm.errorMessage', {
          defaultValue: "We couldn't delete this account: {{errorMessage}}",
          errorMessage:
            error instanceof Error
              ? error.message
              : tErrors('unknown', { defaultValue: 'An unexpected error occurred. Please try again.' }),
        }),
        { confirmLabel: tButtons('okay', { defaultValue: 'Okay' }) },
      );
    }
  }

  // Split per row kind rather than one list with `visible:` flags on every second entry: the row type
  // is discriminated, so a card row genuinely has no Phone or InstitutionName to read.
  function getPersonalDetails(row: AccountRow): DetailRow[] {
    const accountHolderName = {
      label: t('PersonalInformation.accountHolderName', { defaultValue: 'Account Holder Name' }),
      value: (row.Type === 'Bank Account' ? row.AccountHolderName : row.CreditCardHolderName) || '-',
    };

    if (row.Type === 'Credit Card') return [accountHolderName];

    return [
      accountHolderName,
      {
        label: t('PersonalInformation.contactNumber', { defaultValue: 'Contact Number' }),
        value: row.Phone || '-',
      },
      {
        label: t('PersonalInformation.email', { defaultValue: 'Email' }),
        value: row.EmailAddress || '-',
      },
    ];
  }

  function getAccountDetails(row: AccountRow): DetailRow[] {
    const accountType = {
      label: t('CreditCardClientDetails.accountType', { defaultValue: 'Account Type' }),
      value: getAccountTypeLabel(row.Type),
    };

    const currency = {
      label: t('CreditCardClientDetails.currency', { defaultValue: 'Currency' }),
      value: row.Currency || '-',
    };

    if (row.Type === 'Credit Card') {
      return [
        {
          label: t('CreditCardClientDetails.accountNumber', { defaultValue: 'Account Number' }),
          value: (
            <Box flx="row" ai="center" jc="fe" gap="sm">
              {row.CreditCardBrand && <CardLogo width="34px" height="28px" cardBrand={row.CreditCardBrand} />}
              <span>{getMaskedAccountLabel(row) || '-'}</span>
            </Box>
          ),
        },
        {
          label: t('CreditCardClientDetails.expirationDate', { defaultValue: 'Expiration Date' }),
          value: `${row.CreditCardExpiryMonth || ''} / ${row.CreditCardExpiryYear || ''}`,
        },
        accountType,
        currency,
      ];
    }

    return [
      {
        label: t('BankAccountInformation.institutionName', { defaultValue: 'Institution Name' }),
        value: row.InstitutionName || '-',
      },
      {
        label: t('BankAccountInformation.accountNumber', { defaultValue: 'Account Number' }),
        value: getFormattedAccountNumber(row) || '-',
      },
      accountType,
      currency,
    ];
  }

  function getAddressDetails(row: AccountRow): DetailRow[] {
    const country = {
      label: t('BankAccountInformation.country', { defaultValue: 'Country' }),
      // Was rendering the raw ISO code — 'CA' under a "Pays" heading.
      value: getCountryLabel(row.Country) || '-',
    };

    if (row.Type === 'Credit Card') return [country];

    return [
      country,
      {
        label: t('AddressInformation.address', { defaultValue: 'Address' }),
        value: row.Address || '-',
        visible: Boolean(row.Address),
      },
      {
        label: t('AddressInformation.city', { defaultValue: 'City' }),
        value: row.City || '-',
        visible: Boolean(row.City),
      },
      {
        label: t('AddressInformation.province', { defaultValue: 'Province/State' }),
        value: row.Province || '-',
        visible: Boolean(row.Province),
      },
      {
        label: t('AddressInformation.postalCode', { defaultValue: 'Postal / ZIP Code' }),
        value: row.PostalCode || '-',
        visible: Boolean(row.PostalCode),
      },
    ];
  }

  const headingAccountLabel =
    (account?.Type === 'Credit Card' ? getMaskedAccountLabel(account) : getFormattedAccountNumber(account)) || '-';

  const isDefault = Boolean(account?.IsDefault);

  const isDefaultForAnyPurpose =
    isDefault || Boolean(account?.IsOperationalDefault || account?.IsBillingDefault || account?.IsDefaultAccount);

  return (
    <SideDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={
        accountListingType === 'Billing'
          ? t('Title.billingAccountDetails', { defaultValue: 'Billing Account Details' })
          : t('Title.operationalAccountDetails', { defaultValue: 'Operational Account Details' })
      }
      modal={false}
    >
      {account && (
        <Box flx="col" gapSize="16px">
          <Card className={cn('card')}>
            <Box jc="sb" ai="center" width="100%">
              <Box gapSize="16px" ai="center">
                <div className={cn('headingIcon')}>
                  <MaterialSymbol
                    icon={account.Type === 'Credit Card' ? 'credit_card' : 'account_balance'}
                    size="24px"
                    color="#fff"
                  />
                </div>

                <Title level="3">{headingAccountLabel}</Title>
              </Box>

              {isDefault ? (
                <div className={cn('headingDefaultLabel')}>
                  {t('DefaultInformation.default', { defaultValue: 'Default' })}
                </div>
              ) : (
                <UtilButton size="medium" disabled={isDeleting || !canSetDefault} onClick={onSetAsDefault}>
                  {t('Button.setAsDefault', { defaultValue: 'Set as Default' })}
                </UtilButton>
              )}
            </Box>
          </Card>

          {renderActions?.({ disabled: isDeleting })}

          <Card className={cn('card')}>
            <Title level="4" className={cn('title')}>
              {t('subTitle', { defaultValue: 'Account Info' })}
            </Title>

            <DetailRows rowSeparators={false} details={getPersonalDetails(account)} />
            <hr className={cn('divider')} />
            <DetailRows rowSeparators={false} details={getAccountDetails(account)} />
            <hr className={cn('divider')} />
            <DetailRows rowSeparators={false} details={getAddressDetails(account)} />

            {account.Type === 'Bank Account' && account.DateAdded && (
              <Box jc="sb" ai="center" mt="sm" height="auto">
                <div className={cn('caption')}>{`${t('description', { defaultValue: 'Account added on' })} ${
                  account.DateAdded
                }`}</div>
              </Box>
            )}
          </Card>

          {!isDefaultForAnyPurpose && canDelete && (
            <Box jc="fe" pb="sm">
              <NoFillButton colorScheme="destructive" size="large" disabled={isDeleting} onClick={handleDeleteAccount}>
                {isDeleting
                  ? t('Button.deletingAccount', { defaultValue: 'Deleting...' })
                  : t('Button.deleteAccount', { defaultValue: 'Delete Account' })}
              </NoFillButton>
            </Box>
          )}
        </Box>
      )}
    </SideDrawer>
  );
}
